import React, { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';

const CheckIcon = ({ size = 10, style = {} }) => (
  <svg width={size} height={size} viewBox="0 0 10 8" fill="none" style={{ display:'inline-block', verticalAlign:'middle', flexShrink:0, ...style }}>
    <path d="M1 4L3.8 7L9 1" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const API_BASE = process.env.REACT_APP_API_URL || 'https://backend-production-9457.up.railway.app';

export default function ApiKeys() {
  const { getToken } = useAuth();
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyResult, setNewKeyResult] = useState(null);
  const [revoking, setRevoking] = useState(null);
  const [confirmRevoke, setConfirmRevoke] = useState(null);
  const [error, setError] = useState(null);
  const [copiedKey, setCopiedKey] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadKeys(); }, []);

  async function loadKeys() {
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/portal/keys`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { const d = await res.json(); setKeys(d.keys || []); }
    } catch { setError('Could not load API keys.'); }
    finally { setLoading(false); }
  }

  async function createKey() {
    if (!newKeyName.trim()) return;
    setCreating(true); setError(null);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/portal/keys`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newKeyName.trim() }),
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || 'Key creation failed'); }
      setNewKeyResult(await res.json());
      setNewKeyName('');
      await loadKeys();
    } catch (e) { setError(e.message); }
    finally { setCreating(false); }
  }

  async function revokeKey(keyId) {
    setRevoking(keyId); setConfirmRevoke(null);
    try {
      const token = await getToken();
      await fetch(`${API_BASE}/portal/keys/${keyId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      setKeys(prev => prev.filter(k => k.id !== keyId));
    } catch { setError('Could not revoke key.'); }
    finally { setRevoking(null); }
  }

  function copyNewKey() {
    navigator.clipboard.writeText(newKeyResult?.key || '');
    setCopiedKey(true); setTimeout(() => setCopiedKey(false), 2000);
  }

  return (
    <div>
      <div style={s.header}>
        <div>
          <h1 style={s.h1}>API Keys</h1>
          <p style={s.subtitle}>Manage your SenseCritiq API keys for Claude Desktop and integrations.</p>
        </div>
      </div>

      {error && <div style={s.errorBanner}>{error}</div>}

      {/* New key revealed */}
      {newKeyResult && (
        <div style={s.newKeyBanner}>
          <div style={s.newKeyHeader}>
            <span style={s.newKeyTitle}>✦ New key created - copy it now</span>
            <span style={s.newKeyWarn}>This key is only shown once.</span>
          </div>
          <div style={s.keyRow}>
            <code style={s.keyCode}>{newKeyResult.key}</code>
            <button style={s.copyBtn} onClick={copyNewKey}>{copiedKey ? <><CheckIcon size={10} style={{marginRight:4}} />Copied</> : 'Copy'}</button>
          </div>
          <button style={s.dismissBtn} onClick={() => setNewKeyResult(null)}>Dismiss</button>
        </div>
      )}

      {/* Create new key */}
      <div className="scq-card" style={s.card}>
        <h2 style={s.cardTitle}>Create a new key</h2>
        <p style={s.cardDesc}>Give the key a name (e.g. "Claude Desktop" or "Production") to keep track of it.</p>
        <div style={s.createRow} className="scq-create-row">
          <input
            style={s.input}
            type="text"
            placeholder="Key name"
            value={newKeyName}
            onChange={e => setNewKeyName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && createKey()}
          />
          <button style={s.createBtn} onClick={createKey} disabled={creating || !newKeyName.trim()}>
            {creating ? 'Creating…' : '+ Create key'}
          </button>
        </div>
      </div>

      {/* Keys list */}
      <div className="scq-card" style={s.card}>
        <h2 style={{...s.cardTitle, marginBottom:16}}>Your keys</h2>
        {loading ? (
          <div style={s.empty}>Loading keys…</div>
        ) : keys.length === 0 ? (
          <div style={s.empty}>No keys yet. Create one above.</div>
        ) : (
          <div style={{overflowX:'auto', WebkitOverflowScrolling:'touch'}}>
          <table style={{...s.table, minWidth:480}}>
            <thead>
              <tr>{['Name','Prefix','Created','Last used',''].map(h => <th key={h} style={s.th}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {keys.map(k => (
                <tr key={k.id} style={s.tr}>
                  <td style={s.td}>{k.name}</td>
                  <td style={s.td}><code style={s.prefix}>{k.key_preview}</code></td>
                  <td style={s.td}>{k.created_at ? new Date(k.created_at).toLocaleDateString() : '-'}</td>
                  <td style={s.td}>{k.last_used_at ? new Date(k.last_used_at).toLocaleDateString() : 'Never'}</td>
                  <td style={{...s.td, textAlign:'right'}}>
                    {confirmRevoke === k.id ? (
                      <span>
                        <button style={s.revokeConfirmBtn} onClick={() => revokeKey(k.id)} disabled={revoking===k.id}>{revoking===k.id ? 'Revoking…' : 'Confirm revoke'}</button>
                        <button style={s.cancelBtn} onClick={() => setConfirmRevoke(null)}>Cancel</button>
                      </span>
                    ) : (
                      <button style={s.revokeBtn} onClick={() => setConfirmRevoke(k.id)}>Revoke</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {/* MCP config snippet */}
      <div className="scq-card" style={s.card}>
        <h2 style={s.cardTitle}>Claude Desktop config</h2>
        <p style={s.cardDesc}>Add this to your <code style={s.inlineCode}>claude_desktop_config.json</code>:</p>
        <pre style={s.pre}>{`{
  "mcpServers": {
    "sensecritiq": {
      "command": "uvx",
      "args": ["sensecritiq-mcp"],
      "env": {
        "SCQ_API_KEY": "your-key-here"
      }
    }
  }
}`}</pre>
      </div>
    </div>
  );
}

const s = {
  header: { marginBottom:32 },
  h1: { fontFamily:'var(--font-display)', fontSize:28, fontWeight:700, letterSpacing:'-0.02em', color:'var(--color-text)', margin:0 },
  subtitle: { color:'var(--color-muted)', marginTop:6, fontSize:14 },
  errorBanner: { background:'rgba(229,62,62,0.10)', border:'1px solid rgba(229,62,62,0.25)', borderRadius:'var(--radius-md)', padding:'12px 16px', color:'var(--color-danger)', fontSize:13, marginBottom:16 },
  newKeyBanner: { background:'rgba(42,139,122,0.08)', border:'1px solid rgba(42,139,122,0.3)', borderRadius:'var(--radius-lg)', padding:20, marginBottom:20 },
  newKeyHeader: { display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 },
  newKeyTitle: { fontFamily:'var(--font-display)', fontWeight:700, fontSize:14, color:'var(--color-green)' },
  newKeyWarn: { fontSize:12, color:'var(--color-muted)' },
  keyRow: { display:'flex', alignItems:'center', gap:12, marginBottom:12 },
  keyCode: { flex:1, padding:'11px 14px', background:'var(--color-card2)', borderRadius:'var(--radius-lg)', border:'1px solid var(--color-border2)', fontSize:13, fontFamily:'monospace', color:'var(--color-text)', wordBreak:'break-all' },
  copyBtn: { padding:'11px 16px', background:'var(--color-accent-dim)', color:'var(--color-accent)', border:'1px solid var(--color-accent-border)', borderRadius:'var(--radius-lg)', cursor:'pointer', fontSize:13, fontFamily:'var(--font-display)', fontWeight:700, whiteSpace:'nowrap' },
  dismissBtn: { fontSize:12, color:'var(--color-muted)', background:'none', border:'none', cursor:'pointer', padding:'4px 0' },
  card: { background:'var(--color-card)', border:'1px solid var(--color-border)', borderRadius:'var(--radius-xl)', padding:24, marginBottom:20, boxShadow:'var(--shadow-card)' },
  cardTitle: { fontFamily:'var(--font-display)', fontSize:16, fontWeight:700, color:'var(--color-text)', margin:'0 0 8px' },
  cardDesc: { color:'var(--color-muted)', fontSize:13, marginBottom:16, lineHeight:1.6 },
  createRow: { display:'flex', gap:12 },
  input: { flex:1, fontFamily:'var(--font-sans)', fontSize:14, padding:'11px 16px', borderRadius:'var(--radius-lg)', border:'1px solid var(--color-border2)', background:'var(--color-card2)', color:'var(--color-text)', outline:'none' },
  createBtn: { padding:'11px 20px', background:'var(--color-magma)', color:'#fff', border:'none', borderRadius:'var(--radius-lg)', cursor:'pointer', fontSize:14, fontFamily:'var(--font-display)', fontWeight:700, whiteSpace:'nowrap' },
  table: { width:'100%', borderCollapse:'collapse' },
  th: { textAlign:'left', padding:'8px 12px', fontSize:11, fontWeight:600, fontFamily:'var(--font-display)', color:'var(--color-muted2)', borderBottom:'1px solid var(--color-border)', textTransform:'uppercase', letterSpacing:'0.07em' },
  tr: { borderBottom:'1px solid var(--color-border)' },
  td: { padding:'13px 12px', fontSize:14, color:'var(--color-text)' },
  prefix: { fontFamily:'monospace', background:'var(--color-accent-dim)', padding:'2px 7px', borderRadius:4, fontSize:12, color:'var(--color-accent)' },
  revokeBtn: { padding:'6px 12px', background:'rgba(229,62,62,0.10)', color:'var(--color-danger)', border:'1px solid rgba(229,62,62,0.2)', borderRadius:'var(--radius-md)', cursor:'pointer', fontSize:12, fontWeight:600 },
  revokeConfirmBtn: { padding:'6px 12px', background:'var(--color-danger)', color:'#fff', border:'none', borderRadius:'var(--radius-md)', cursor:'pointer', fontSize:12, fontWeight:600, marginRight:6 },
  cancelBtn: { padding:'6px 10px', background:'none', color:'var(--color-muted)', border:'1px solid var(--color-border)', borderRadius:'var(--radius-md)', cursor:'pointer', fontSize:12 },
  inlineCode: { fontFamily:'monospace', background:'var(--color-accent-dim)', padding:'1px 6px', borderRadius:4, fontSize:12, color:'var(--color-accent)' },
  pre: { background:'var(--color-card2)', border:'1px solid var(--color-border)', borderRadius:'var(--radius-md)', padding:18, fontSize:13, fontFamily:'monospace', color:'var(--color-muted)', overflowX:'auto', lineHeight:1.7, margin:0 },
  empty: { color:'var(--color-muted)', fontSize:14, padding:'20px 0', textAlign:'center' },
};
